import { alpha, Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { keyframes } from "@emotion/react";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useEffect } from "react";
import { getRuntimeEnv } from "../../providers/getRuntimeEnv";
import { useTranslation } from "../../hooks/useTranslation";
import { useNavigate } from "react-router";

const pulse = keyframes`
  0% { opacity: 0.6; transform: scale(0.98); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.6; transform: scale(0.98); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const swing = keyframes`
  0% { transform: rotate(-15deg); }
  100% { transform: rotate(15deg); }
`;

export const Maintenance = () => {
  const { translate } = useTranslation("pages.maintenance");
  const runtimeEnv = getRuntimeEnv();
  const theme = useTheme();
  const navigate = useNavigate();

  const isDevEnv = runtimeEnv === "dev";
  const appName = isDevEnv ? "SyncroDev" : "Syncrosale";
  const logo = isDevEnv ? "/icons/logo-dev.svg" : "/icons/logo.svg";

  const handleRefresh = () => navigate("/");
  useEffect(() => {
    const interval = setInterval(() => {
      navigate("/");
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 4, md: 10 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Stack spacing={3} flex={1}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            letterSpacing: "-0.5px",
          }}
        >
          {translate("title", { appName })}
        </Typography>

        <Typography
          sx={{
            color: "#9CA3AF",
            fontSize: "1.1rem",
            lineHeight: 1.6,
          }}
        >
          {translate("description")}
        </Typography>

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: "0.9rem",
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        >
          ● {translate("status")}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleRefresh}
            sx={{
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.2,
              borderRadius: "10px",
            }}
          >
            {translate("retry")}
          </Button>

          <Button
            variant="text"
            disabled
            sx={{
              color: "#6B7280",
              textTransform: "none",
            }}
            startIcon={
              <AutorenewIcon
                sx={{
                  animation: `${rotate} 2s linear infinite`,
                }}
              />
            }
          >
            {translate("autoRefresh")}
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          width: "50%",
          height: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: `
              radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.35,
              )} 0%, transparent 30%),
              radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.15,
              )} 0%, transparent 75%)
            `,
            filter: "blur(50px)",
          }}
        />

        <Box
          sx={{
            width: 320,
            height: 320,
            borderRadius: "24px",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="SyncroSale Logo"
            sx={{
              width: "100%",
              maxWidth: 380,
              height: "auto",
              userSelect: "none",
              animation: `${swing} 8s ease-in-out infinite alternate`,
              transformOrigin: "center",
              filter: `
                drop-shadow(0 0 20px ${alpha(theme.palette.primary.main, 0.4)})
                drop-shadow(0 0 40px ${alpha(theme.palette.primary.main, 0.2)})
              `,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
